import wootConstants from 'dashboard/constants/globals';

const { CUSTOM_SLA_MISS_TYPES } = wootConstants;

const BREACHED_STATUSES = ['active_with_misses', 'missed'];

export const TIMER_TIER = {
  GREEN: 'green',
  YELLOW: 'yellow',
  ORANGE: 'orange',
  RED: 'red',
};

// Formats a duration in seconds as a stopwatch-style hh:mm:ss (or mm:ss when under an
// hour), always counting up from zero regardless of sign — callers decide whether the
// value represents elapsed or overdue time.
export const formatCountdown = totalSeconds => {
  const seconds = Math.max(0, Math.round(Math.abs(totalSeconds || 0)));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const pad = value => String(value).padStart(2, '0');

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }
  return `${pad(minutes)}:${pad(secs)}`;
};

export const isCustomSlaBreached = appliedSla => {
  if (!appliedSla) return false;
  return BREACHED_STATUSES.includes(appliedSla.sla_status);
};

export const latestCustomSlaBreachType = (events = []) => {
  if (!events.length) return null;
  return events[events.length - 1].event_type;
};

// Whether a breach event of this specific type (first_response/next_response/resolution)
// was ever recorded, independent of breaches of other types on the same applied SLA.
export const hasCustomSlaBreachOfType = (events, type) =>
  events.some(event => event.event_type === type);

// A reply only counts as "the first response" if it happened at or after the SLA
// actually started (`appliedSla.created_at`) — a bot reply sent while the conversation
// was still pending, before hand-off, must not count (mirrors the backend's
// `qualifying_first_reply_at`, see CustomSla::EvaluateAppliedSlaService).
const hasQualifyingFirstReply = appliedSla =>
  appliedSla.first_response_actual_seconds != null;

// The threshold currently being counted down: first response (until the first agent
// reply), next response (while waiting on an agent reply to a customer message), or
// resolution (while the conversation is open) — in that priority order, matching the
// backend's own evaluation order. First-response and resolution windows start at
// `appliedSla.created_at`, not the conversation's own creation time — the SLA clock
// starts on hand-off from a bot to a human (see `CustomSlaConversation`), which can be
// well after the conversation itself was created.
const currentThresholdWindow = (conversation, appliedSla) => {
  const {
    sla_first_response_time_threshold: frt,
    sla_next_response_time_threshold: nrt,
    sla_resolution_time_threshold: rt,
  } = appliedSla;

  if (!hasQualifyingFirstReply(appliedSla) && frt) {
    return {
      type: CUSTOM_SLA_MISS_TYPES.FIRST_RESPONSE,
      start: appliedSla.created_at,
      threshold: frt,
    };
  }
  if (conversation.waiting_since && nrt) {
    return {
      type: CUSTOM_SLA_MISS_TYPES.NEXT_RESPONSE,
      start: conversation.waiting_since,
      threshold: nrt,
    };
  }
  if (conversation.status !== 'resolved' && rt) {
    return {
      type: CUSTOM_SLA_MISS_TYPES.RESOLUTION,
      start: appliedSla.created_at,
      threshold: rt,
    };
  }
  return null;
};

// 0-50% of the threshold: green. 50-75%: yellow. 75-100%: orange. 100%+: red.
export const tierForProgress = progress => {
  if (progress >= 1) return TIMER_TIER.RED;
  if (progress >= 0.75) return TIMER_TIER.ORANGE;
  if (progress >= 0.5) return TIMER_TIER.YELLOW;
  return TIMER_TIER.GREEN;
};

// The once-only thresholds (first response, resolution) each get a single Met/Breached
// history entry once fulfilled, with how long they actually took. Next-response is
// repeating (an applied SLA can have many next-response cycles) and has no single
// "took X" duration recorded for it, so it's covered separately by the met/missed counts
// (`CustomSlaNextResponseCounts`) rather than by this history.
export const buildCustomSlaHistory = ({ appliedSla, events = [] }) => {
  if (!appliedSla) return [];

  const entries = [];

  if (appliedSla.first_response_actual_seconds != null) {
    entries.push({
      type: CUSTOM_SLA_MISS_TYPES.FIRST_RESPONSE,
      isBreached: hasCustomSlaBreachOfType(
        events,
        CUSTOM_SLA_MISS_TYPES.FIRST_RESPONSE
      ),
      durationSeconds: appliedSla.first_response_actual_seconds,
    });
  }

  if (appliedSla.resolution_actual_seconds != null) {
    entries.push({
      type: CUSTOM_SLA_MISS_TYPES.RESOLUTION,
      isBreached: hasCustomSlaBreachOfType(
        events,
        CUSTOM_SLA_MISS_TYPES.RESOLUTION
      ),
      durationSeconds: appliedSla.resolution_actual_seconds,
    });
  }

  return entries;
};

// Computes the live "cronômetro" state for a conversation's custom SLA: which threshold
// is currently being counted down (if any) and its color tier based on percentage of the
// threshold elapsed. Nothing is displayed once a threshold is no longer actively running.
export const evaluateCustomSlaStatus = ({
  conversation = {},
  appliedSla,
  events = [],
}) => {
  if (!appliedSla) {
    return {
      hasActiveCountdown: false,
      tier: null,
      type: null,
      progress: null,
      elapsedSeconds: null,
      remainingSeconds: null,
      thresholdSeconds: null,
      actualSeconds: null,
      isBreached: false,
      breachType: null,
      windowStart: null,
    };
  }

  const isBreached = isCustomSlaBreached(appliedSla);
  const breachType = latestCustomSlaBreachType(events);
  const current = currentThresholdWindow(conversation, appliedSla);
  const now = Math.floor(Date.now() / 1000);

  if (current) {
    const elapsedSeconds = now - current.start;
    const progress =
      current.threshold > 0 ? elapsedSeconds / current.threshold : 0;

    // The color tier is judged solely on this specific window's own elapsed-vs-threshold
    // progress — not on `isBreached` (the applied SLA's account-wide pinned status from a
    // possibly unrelated, already-finished threshold). Using the pinned status here made a
    // brand-new countdown (e.g. resolution, just starting after an earlier next-response
    // miss) render red from the very first second, which misleads the agent about how much
    // time is actually left on the timer they're looking at. A window that is itself already
    // overdue is still correctly red, since `tierForProgress` returns red at 100%+ progress.
    return {
      hasActiveCountdown: true,
      tier: tierForProgress(progress),
      type: current.type,
      progress,
      elapsedSeconds,
      remainingSeconds: current.threshold - elapsedSeconds,
      thresholdSeconds: current.threshold,
      actualSeconds: null,
      isBreached,
      breachType: breachType || current.type,
      // Identifies this specific running window (e.g. one particular next-response cycle,
      // which repeats with a new `waiting_since` each time) so callers — the percentage-
      // based alert in `CustomSlaBadge.vue` — can tell a genuinely new window apart from
      // the same window ticking forward, and alert once per window rather than once ever.
      windowStart: current.start,
    };
  }

  // Once resolved, the badge keeps showing a resolution-specific Met/Breached outcome
  // (it must not disappear on resolve) — judged by whether a `resolution` breach event
  // was recorded, not the applied SLA's account-wide pinned status, so an earlier
  // unrelated first-response/next-response breach doesn't turn a timely resolution red.
  // This relies on `events`/`custom_applied_sla` being kept fresh in real time (see the
  // `Conversations::EventDataPresenter` fix), which earlier made this judgment unsafe.
  if (
    conversation.status === 'resolved' &&
    appliedSla.sla_resolution_time_threshold
  ) {
    const resolutionBreached = hasCustomSlaBreachOfType(
      events,
      CUSTOM_SLA_MISS_TYPES.RESOLUTION
    );

    return {
      hasActiveCountdown: false,
      tier: resolutionBreached ? TIMER_TIER.RED : TIMER_TIER.GREEN,
      type: CUSTOM_SLA_MISS_TYPES.RESOLUTION,
      progress: null,
      elapsedSeconds: null,
      remainingSeconds: null,
      thresholdSeconds: null,
      actualSeconds: appliedSla.resolution_actual_seconds ?? null,
      isBreached: resolutionBreached,
      breachType,
      windowStart: null,
    };
  }

  // Any other fulfilled-but-not-resolved state (e.g. first response answered while the
  // conversation is still open) shows nothing — only the live countdown/overdue text is
  // shown while something is actively running.
  return {
    hasActiveCountdown: false,
    tier: isBreached ? TIMER_TIER.RED : TIMER_TIER.GREEN,
    type: null,
    progress: null,
    elapsedSeconds: null,
    remainingSeconds: null,
    thresholdSeconds: null,
    actualSeconds: null,
    isBreached,
    breachType,
    windowStart: null,
  };
};
